import React, { useEffect, useState } from 'react';

import { format, isBefore, parseISO } from 'date-fns';
import { Container } from './styles';

import loan from '~/assets/defaults/loan-amount.png';
import Totalizers from '~/components/Totalizers';
import Graph, { ISerie } from '~/components/Graph';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';

interface IContract {
  id: string;
  loan_amount: string;
  date: string;
  return: string;
  payment_amount: string;
  payment_date: string;
  maturity_date: string;
}

interface ITableData {
  id: string;
  loanAmount: string;
  contractDate: string;
  return: string;
  paymentAmount: string;
  paymentDate: string;
  maturityDate: string;
}

interface ITotals {
  total_loan: number;
  total_repaid: number;
  total_to_repay: number;
  monthly_payments: number;
  next_payment_date: string;
}

const Dashboard: React.FC = () => {
  const [tableData, setTableData] = useState<ITableData[]>([]);
  const [dataGraph, setDataGraph] = useState<string[]>([]);
  const [months, setMonths] = useState<ISerie[]>([]);

  useEffect(() => {
    api.get<IContract[]>('contracts').then((response) => {
      const data = response.data.map<ITableData>((contract) => ({
        id: contract.id,
        loanAmount: formatPrice(parseFloat(contract.loan_amount)).replace(
          '$',
          ''
        ),
        contractDate: format(parseISO(contract.date), 'MM/dd/yy'),
        return: contract.return,
        paymentAmount: formatPrice(parseFloat(contract.payment_amount)).replace(
          '$',
          ''
        ),
        paymentDate: contract.payment_date,
        maturityDate: format(parseISO(contract.maturity_date), 'MM/dd/yy'),
      }));

      const fisrtData = response.data.reduce((max, obj) => {
        return parseISO(obj.date) < parseISO(max.date) ? obj : max;
      }, response.data[0]);

      const latestDate = response.data.reduce((max, obj) => {
        return parseISO(obj.maturity_date) > parseISO(max.maturity_date)
          ? obj
          : max;
      }, response.data[0]);

      const firstDate = parseISO(fisrtData.date);
      const firstDateAux = parseISO(fisrtData.date);
      const lastDate = parseISO(latestDate.maturity_date);

      firstDateAux.setDate(15);
      if (!isBefore(firstDate, firstDateAux)) {
        firstDate.setMonth(lastDate.getMonth() + 1);
      }
      if (!isBefore(lastDate, new Date())) {
        lastDate.setMonth(lastDate.getMonth() + 1);
      }

      const monthsDiff =
        (lastDate.getFullYear() - firstDate.getFullYear()) * 12 +
        (lastDate.getMonth() - firstDate.getMonth()) +
        1;

      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const monthsData = Array.from({ length: monthsDiff }, (_, index) => {
        const currentMonthIndex = (firstDate.getMonth() + index) % 12;
        return {
          value: monthNames[currentMonthIndex],
          selected: new Date().getMonth() === currentMonthIndex,
        };
      });

      setMonths(monthsData);
      setTableData(data);
    });
  }, []);

  useEffect(() => {
    api.get<ITotals>('contracts/totals').then((response) => {
      const total = response.data.total_repaid + response.data.total_to_repay;
      const totalData = total / months.length;
      const data = [];
      for (let i = 0; i < months.length; i += 1) {
        data.push(formatPrice(totalData * (i + 1)).replace('$', ''));
      }

      setDataGraph(data);
    });
  }, [months.length]);

  return (
    <Container>
      <div className="container">
        <div className="row">
          <div className="col-12 px-4 py-5 px-lg-5">
            <Totalizers />
            <div className="row">
              <div className="col-12 d-none d-lg-block">
                {months.length > 0 && (
                  <Graph data={dataGraph} series={months} />
                )}
              </div>
              <div className="col-12 px-5">
                <hr />
              </div>
              <div className="d-none d-lg-block col-12 header-table mb-3">
                <div className="row">
                  <div className="col-lg-2">
                    <h2>Loan Amount</h2>
                  </div>
                  <div className="col-lg-2">
                    <h2>Contract Date</h2>
                  </div>
                  <div className="col-lg-2">
                    <h2>Return %</h2>
                  </div>
                  <div className="col-lg-2">
                    <h2>Payment Amount</h2>
                  </div>
                  <div className="col-lg-2">
                    <h2>Payment Date</h2>
                  </div>
                  <div className="col-lg-2">
                    <h2>Maturity Date</h2>
                  </div>
                </div>
              </div>
              {tableData.map((data) => (
                <div className="col-12 row-lg-table mb-3">
                  <div className="row">
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Loan Amount</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 mb-3 mb-lg-0">
                      <h3 className=" d-flex align-items-center">
                        <img
                          width={52}
                          height={52}
                          src={loan}
                          alt="Loan"
                          className="me-lg-3"
                        />
                        <span className="loan-amount">${data.loanAmount}</span>
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Contract Date</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 d-flex align-items-center mb-3 mb-lg-0">
                      <h3 className="mx-auto mx-lg-0 padding">
                        {data.contractDate}
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Return %</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 d-flex align-items-center mb-3 mb-lg-0">
                      <h3 className="mx-auto mx-lg-0 padding">
                        Principal + {data.return}%
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Payment Amount</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 d-flex align-items-center mb-3 mb-lg-0">
                      <h3 className="mx-auto mx-lg-0 padding">
                        ${data.paymentAmount}
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Payment Date</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 d-flex align-items-center mb-3 mb-lg-0">
                      <h3 className="mx-auto mx-lg-0 padding">
                        {data.paymentDate}
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none header-table text-center mb-3">
                      <h2>Maturity Date</h2>
                    </div>
                    <div className="row-md-table col-12 col-lg-2 d-flex align-items-center">
                      <h3 className="mx-auto mx-lg-0 padding">
                        {data.maturityDate}
                      </h3>
                    </div>
                    <div className="col-12 d-lg-none">
                      <hr className="px-5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Dashboard;
